import axios, { AxiosResponse } from 'axios';
import { VerificationResponse } from '../types';

// Create axios instance with base configuration
const api = axios.create({
    baseURL: 'https://api.vastavik.org/api/v1',
    timeout: 30000, // 30 seconds timeout for large files
});

// Add request interceptor for logging
api.interceptors.request.use(
    (config) => {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
    },
    (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
api.interceptors.response.use(
    (response) => {
        console.log(`API Response: ${response.status} ${response.config.url}`);
        return response;
    },
    (error) => {
        console.error('API Response Error:', error.response?.data || error.message);
        return Promise.reject(error);
    }
);

/**
 * Verify media file authenticity
 */
export const verifyMedia = async (file: File): Promise<VerificationResponse> => {
    try {
        // Validate file before sending
        if (!file) {
            throw new Error('No file provided');
        }

        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
            throw new Error('Please select a media file (images or videos only)');
        }

        if (file.size > 50 * 1024 * 1024) {
            throw new Error('File is too large. Please select a file smaller than 50MB');
        }

        // Create form data
        const formData = new FormData();
        formData.append('image', file); // Backend expects 'image' field name

        // Make API call
        const response: AxiosResponse<VerificationResponse> = await api.post(
            '/verify/image',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );

        return response.data;
    } catch (error) {
        console.error('Verification error:', error);

        if (axios.isAxiosError(error)) {
            if (error.response) {
                // Server responded with error status
                const serverError = error.response.data?.error || error.response.data?.message;
                throw new Error(serverError || `Server error: ${error.response.status}`);
            } else if (error.request) {
                // Request was made but no response received
                throw new Error('Unable to connect to verification server. Please check your internet connection.');
            }
        }

        // Other errors (validation, etc.)
        throw error;
    }
};

/**
 * Health check endpoint (optional)
 */
export const checkHealth = async (): Promise<any> => {
    try {
        const response = await api.get('/health');
        return response.data;
    } catch (error) {
        console.error('Health check failed:', error);
        throw error;
    }
};

/**
 * Verify media file by owner key or media ID
 */
export const verifyByOwnerKey = async (ownerKey: string): Promise<VerificationResponse> => {
    try {
        // Validate input
        if (!ownerKey || !ownerKey.trim()) {
            throw new Error('Creator key is required');
        }

        // Make API call - using GET request with owner key as parameter
        const response: AxiosResponse<VerificationResponse> = await api.get(
            `/media/owner-key/${encodeURIComponent(ownerKey.trim())}`
        );

        return response.data;
    } catch (error) {
        console.error('Creator key verification error:', error);

        if (axios.isAxiosError(error)) {
            if (error.response) {
                // Server responded with error status
                const serverError = error.response.data?.error || error.response.data?.message;
                if (error.response.status === 404) {
                    throw new Error('No media found with this creator key');
                }
                throw new Error(serverError || `Server error: ${error.response.status}`);
            } else if (error.request) {
                // Request was made but no response received
                throw new Error('Unable to connect to verification server. Please check your internet connection.');
            }
        }

        // Other errors (validation, etc.)
        throw error;
    }
};

export default api;
